<template>
  <div class="timeline">
    <!-- Timeline Toggle -->
    <div class="timeline-toggle">
      <button 
        :class="['toggle-btn', 'videos-toggle', { active: timelineMode === 'videos' }]"
        @click="switchTimelineMode('videos')"
      >
        <span class="toggle-text">Videos</span>
        <button 
          v-if="timelineMode === 'videos'"
          class="filter-icon-btn"
          @click.stop="toggleFilters"
          :class="{ active: showFilters }"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z"/>
          </svg>
        </button>
      </button>
      <button 
        :class="['toggle-btn', { active: timelineMode === 'all-activity' }]"
        @click="switchTimelineMode('all-activity')"
      >
        All Activity
      </button>
    </div>

    <!-- Filter Dropdowns -->
    <div v-if="showFilters && timelineMode === 'videos'" class="filters-container">
      <div class="filter-row">
        <div class="filter-group">
          <label class="filter-label">Minimum Boat Speed</label>
          <select v-model="filters.minSpeed" @change="applyFilters" class="filter-select">
            <option value="">All Speeds</option>
            <option v-for="speed in speedOptions" :key="speed.value" :value="speed.value">
              {{ speed.label }}
            </option>
          </select>
        </div>
        
        <div class="filter-group">
          <label class="filter-label">Minimum Rope Length</label>
          <select v-model="filters.minRope" @change="applyFilters" class="filter-select">
            <option value="">All Lengths</option>
            <option v-for="rope in ropeOptions" :key="rope.value" :value="rope.value">
              {{ rope.label }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Highlight Component -->
    <div v-if="highlight" class="highlight-container">
      <button class="highlight-close" @click="closeHighlight" title="Dismiss highlight">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 7.293l3.646-3.647a.5.5 0 01.708.708L8.707 8l3.647 3.646a.5.5 0 01-.708.708L8 8.707l-3.646 3.647a.5.5 0 01-.708-.708L7.293 8 3.646 4.354a.5.5 0 11.708-.708L8 7.293z"/>
        </svg>
      </button>
      <div class="highlight-content">
        <div class="highlight-message">{{ highlight.message }}</div>
        <div v-if="highlight.target_user" class="highlight-user" @click="goToUserProfile(highlight.target_user.username)">
          <img :src="highlight.target_user.avatar" :alt="highlight.target_user.username" class="highlight-avatar">
          <span class="highlight-username">@{{ highlight.target_user.username }}</span>
        </div>
      </div>
    </div>

    <!-- Posts Container with Pull-to-Refresh -->
    <div 
      class="posts-container"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      :style="{ transform: `translateY(${pullDistance}px)` }"
    >
      <!-- Pull-to-Refresh Indicator -->
      <div v-if="isPulling" class="pull-indicator" :style="{ opacity: pullOpacity, marginTop: isRefreshing ? '-110px' : '0' }">
        <LoadingSpinner v-if="isRefreshing" />
        <div v-else class="pull-text">Pull to refresh</div>
      </div>

      <LoadingSpinner v-if="loadingtimeline && !isRefreshing" />
      <template v-else-if="!isRefreshing">
        <template v-for="item in items" :key="getItemKey(item)">
          <!-- Render PostCard for posts -->
          <PostCard 
            v-if="item.type === 'post'"
            :post="item.data"
            :ref="(el) => { if (el) postCards[item.data.id] = el }"
            @tips-count-updated="(count) => updateTipsCount(item.data.id, count)"
            @like-updated="(likeData) => updateLike(item.data.id, likeData)"
            @post-deleted="handlePostDeleted"
          />
          
          <!-- Render TipCard for comments -->
          <TipCard 
            v-else-if="item.type === 'comment'"
            :tip="item"
          />
        </template>
        
        <!-- Loading spinner for infinite scroll -->
        <div v-if="loading && items.length > 0" class="infinite-scroll-loading">
          <LoadingSpinner />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import PostCard from '@/components/PostCard.vue';
import TipCard from '@/components/TipCard.vue';

export default {
  name: 'HomeTimeline',
  components: {
    LoadingSpinner,
    PostCard,
    TipCard
  },
  data() {
    return {
      items: [], // Changed from posts to items to handle both posts and comments
      videosItems: [], // Cache for videos timeline
      allActivityItems: [], // Cache for all activity timeline
      userID: null,
      offset: 0,
      videosOffset: 0, // Separate offset for videos timeline
      allActivityOffset: 0, // Separate offset for all activity timeline
      limit: 5,
      loading: false,
      loadingtimeline: true,
      postCards: {},
      timelineMode: 'videos', // Default to 'videos' instead of 'recommended'
      highlight: null, // Added highlight data property
      // Pull-to-refresh variables
      touchStartY: 0,
      pullDistance: 0,
      isPulling: false,
      isRefreshing: false,
      pullThreshold: 60,
      showFilters: false,
      filters: {
        minSpeed: '',
        minRope: ''
      },
      speedOptions: [
        { value: '58', label: '58kph - 36mph' },
        { value: '55', label: '55kph - 34mph' },
        { value: '52', label: '52kph - 32mph' },
        { value: '49', label: '49kph - 30mph' },
        { value: '46', label: '46kph - 28mph' },
        { value: '43', label: '43kph - 26mph' },
        { value: '40', label: '40kph - 25mph' },
        { value: '37', label: '37kph - 23mph' }
      ],
      ropeOptions: [
        { value: '18', label: '18.25 meter - 15 Off' },
        { value: '16', label: '16.00 meter - 22 Off' },
        { value: '14', label: '14.25 meter - 28 Off' },
        { value: '13', label: '13.00 meter - 32 Off' },
        { value: '12', label: '12.00 meter - 35 Off' },
        { value: '11', label: '11.25 meter - 38 Off' },
        { value: '10.75', label: '10.75 meter - 39.5 Off' },
        { value: '10.25', label: '10.25 meter - 41 Off' },
        { value: '9', label: '9.75 meter - 43 Off' }
      ],
    };
  },
  computed: {
    pullOpacity() {
      return Math.min(this.pullDistance / this.pullThreshold, 1);
    },
    // Legacy computed property for backward compatibility
    posts() {
      return this.items.filter(item => item.type === 'post').map(item => item.data);
    }
  },
  metaInfo: {
    title: 'Home Timeline',
    meta: [
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no' }
    ]
  },
  created() {
    // Check for timeline mode from query parameters (e.g., from post creation)
    if (this.$route.query.mode === 'all-activity') {
      this.timelineMode = 'all-activity';
    }
    
    this.fetchHighlight(); // Fetch highlight first
    this.fetchItems();
    this.setupScrollListener();
  },
  methods: {
    getItemKey(item) {
      return `${item.type}-${item.id}`;
    },

    // Pull-to-refresh methods
    handleTouchStart(e) {
      if (window.scrollY > 0) return; // Only allow pull when at top
      this.touchStartY = e.touches[0].clientY;
    },

    handleTouchMove(e) {
      if (window.scrollY > 0 || this.isRefreshing) return;
      
      const touchY = e.touches[0].clientY;
      const pullDistance = Math.max(0, (touchY - this.touchStartY) * 0.5); // Reduce sensitivity
      
      if (pullDistance > 0) {
        e.preventDefault(); // Prevent default scroll behavior
        this.pullDistance = Math.min(pullDistance, this.pullThreshold * 1.5);
        this.isPulling = true;
      }
    },

    async handleTouchEnd() {
      if (this.pullDistance >= this.pullThreshold && !this.isRefreshing) {
        this.isRefreshing = true;
        await this.refreshTimeline();
      }
      
      // Reset pull state
      this.pullDistance = 0;
      this.isPulling = false;
      this.isRefreshing = false;
    },

    async refreshTimeline() {
      try {
        // Reset timeline state
        this.items = [];
        this.offset = 0;
        // Also reset both caches to ensure fresh data on next switch
        this.videosItems = [];
        this.videosOffset = 0;
        this.allActivityItems = [];
        this.allActivityOffset = 0;
        this.postCards = {};
        
        // Fetch fresh items for current timeline mode
        await this.fetchItems();
        
        // Small delay for better UX
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('Error refreshing timeline:', error);
      }
    },

    async fetchHighlight() {
      try {
        console.log('Fetching highlight...');
        const response = await axios.get('https://api.6buoys.com/api/highlights/latest/', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });
        
        console.log('Highlight API response:', response.data);
        
        // Handle both response formats:
        // 1. Direct highlight object: {"id": 1, "message": "...", ...}
        // 2. Wrapped response: {"highlight": null}
        if (response.data.highlight !== undefined) {
          // Case: {"highlight": null} or {"highlight": {...}}
          this.highlight = response.data.highlight;
        } else if (response.data.id) {
          // Case: Direct highlight object with id
          this.highlight = response.data;
        } else {
          // Fallback: no highlight
          this.highlight = null;
        }
        
        console.log('Highlight set to:', this.highlight);
      } catch (error) {
        console.error('Error fetching highlight:', error);
        this.highlight = null;
      }
    },

    async fetchItems() {
      if (this.loading || this.items.length >= 1000) return;
      this.loading = true;

      try {
        // Use different endpoint based on timeline mode
        const endpoint = this.timelineMode === 'all-activity' 
          ? 'https://api.6buoys.com/api/all-activity/'
          : 'https://api.6buoys.com/api/post-list/';

        // Build request parameters
        const params = {
          limit: this.limit,
          offset: this.offset,
        };

        // Add filter parameters for videos timeline
        if (this.timelineMode === 'videos') {
          if (this.filters.minSpeed) {
            params.min_speed = this.filters.minSpeed;
          }
          if (this.filters.minRope) {
            params.min_rope = this.filters.minRope;
          }
        }

        const response = await axios.get(endpoint, {
          params,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });

        let newItems;
        
        if (this.timelineMode === 'all-activity') {
          // All activity endpoint returns items with type and data structure
          newItems = response.data.map(item => ({
            ...item,
            // Add required fields for posts
            ...(item.type === 'post' && {
              data: {
                ...item.data,
                showTips: false,
                tips: [],
                tips_count: item.data.tips_count || 0,
                likes_count: item.data.likes_count || 0,
                liked_by_user: item.data.liked_by_user || false,
                like_id: item.data.like_id || null,
                author: item.data.author || {
                  id: null,
                  username: '',
                  avatar: 'default-avatar.png',
                  first_name: 'Unknown',
                  last_name: ''
                }
              }
            })
          }));
        } else {
          // Videos timeline - convert posts to item format
          newItems = response.data.map(post => ({
            type: 'post',
            id: post.id,
            created: post.created,
            data: {
              ...post,
              showTips: false,
              tips: [],
              tips_count: post.tips_count || 0,
              likes_count: post.likes_count || 0,
              liked_by_user: post.liked_by_user || false,
              like_id: post.like_id || null,
              author: post.author || {
                id: null,
                username: '',
                avatar: 'default-avatar.png',
                first_name: 'Unknown',
                last_name: ''
              }
            }
          }));
        }

        this.items = [...this.items, ...newItems];
        this.offset += this.limit;

        // Update the appropriate cache with new items
        if (this.timelineMode === 'all-activity') {
          this.allActivityItems = [...this.items];
          this.allActivityOffset = this.offset;
          // Load videos timeline in background if not cached (only on first load)
          if (this.videosItems.length === 0 && this.items.length === this.limit) {
            this.loadVideosInBackground();
          }
        } else {
          // Note: For videos timeline with filters, we don't cache as the results depend on filter state
          if (!this.hasActiveFilters()) {
            this.videosItems = [...this.items];
            this.videosOffset = this.offset;
          }
          // Load all activity timeline in background if not cached (only on first load)
          if (this.allActivityItems.length === 0 && this.items.length === this.limit) {
            this.loadAllActivityInBackground();
          }
        }

        // Increment view count for each new post
        newItems.forEach(item => {
          if (item.type === 'post') {
            this.incrementPostView(item.data.id);
          }
        });

        // Trigger preloading of tips for all new posts
        this.$nextTick(() => {
          newItems.forEach(item => {
            if (item.type === 'post') {
              const postCard = this.postCards[item.data.id];
              if (postCard && postCard.preloadTips) {
                postCard.preloadTips();
              }
            }
          });
        });

      } catch (error) {
        console.error('Error fetching items:', error);
      } finally {
        this.loading = false;
        this.loadingtimeline = false;
      }
    },

    async loadAllActivityInBackground() {
      try {
        const response = await axios.get('https://api.6buoys.com/api/all-activity/', {
          params: {
            limit: this.limit,
            offset: this.allActivityOffset,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });

        const newItems = response.data.map(item => ({
          ...item,
          // Add required fields for posts
          ...(item.type === 'post' && {
            data: {
              ...item.data,
              showTips: false,
              tips: [],
              tips_count: item.data.tips_count || 0,
              likes_count: item.data.likes_count || 0,
              liked_by_user: item.data.liked_by_user || false,
              like_id: item.data.like_id || null,
              author: item.data.author || {
                id: null,
                username: '',
                avatar: 'default-avatar.png',
                first_name: 'Unknown',
                last_name: ''
              }
            }
          })
        }));

        this.allActivityItems = [...this.allActivityItems, ...newItems];
        this.allActivityOffset += this.limit;

      } catch (error) {
        console.error('Error loading all activity items in background:', error);
      }
    },

    async loadVideosInBackground() {
      try {
        const response = await axios.get('https://api.6buoys.com/api/post-list/', {
          params: {
            limit: this.limit,
            offset: this.videosOffset,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });

        const newItems = response.data.map(post => ({
          type: 'post',
          id: post.id,
          created: post.created,
          data: {
            ...post,
            showTips: false,
            tips: [],
            tips_count: post.tips_count || 0,
            likes_count: post.likes_count || 0,
            liked_by_user: post.liked_by_user || false,
            like_id: post.like_id || null,
            author: post.author || {
              id: null,
              username: '',
              avatar: 'default-avatar.png',
              first_name: 'Unknown',
              last_name: ''
            }
          }
        }));

        this.videosItems = [...this.videosItems, ...newItems];
        this.videosOffset += this.limit;

      } catch (error) {
        console.error('Error loading videos items in background:', error);
      }
    },

    switchTimelineMode(mode) {
      if (this.timelineMode === mode) return; // No change needed
      
      // Reset filters when switching away from videos mode
      if (this.timelineMode === 'videos' && mode !== 'videos') {
        this.showFilters = false;
        this.filters = {
          minSpeed: '',
          minRope: ''
        };
      }
      
      this.timelineMode = mode;
      
      if (mode === 'all-activity') {
        // Switch to all activity timeline
        if (this.allActivityItems.length > 0) {
          // Use cached all activity items - instant switch!
          this.items = [...this.allActivityItems];
          this.offset = this.allActivityOffset;
          this.postCards = {};
          this.loadingtimeline = false;
        } else {
          // All activity items not cached yet, load them
          this.items = [];
          this.offset = 0;
          this.postCards = {};
          this.loadingtimeline = true;
          this.fetchItems();
        }
      } else {
        // Switch to videos timeline
        // Check if we have filters active or cached videos
        if (!this.hasActiveFilters() && this.videosItems.length > 0) {
          // Use cached videos items - instant switch!
          this.items = [...this.videosItems];
          this.offset = this.videosOffset;
          this.postCards = {};
          this.loadingtimeline = false;
        } else {
          // Videos items not cached yet or filters are active, load them
          this.items = [];
          this.offset = 0;
          this.postCards = {};
          this.loadingtimeline = true;
          this.fetchItems();
        }
      }
    },

    setupScrollListener() {
      window.addEventListener('scroll', () => {
        if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 500 && !this.loading) {
          this.fetchItems();
        }
      });
    },

    updateTipsCount(postId, count) {
      const item = this.items.find(item => item.type === 'post' && item.data.id === postId);
      if (item) {
        item.data.tips_count = count;
      }
    },

    updateLike(postId, likeData) {
      const item = this.items.find(item => item.type === 'post' && item.data.id === postId);
      if (item) {
        item.data.liked_by_user = likeData.liked_by_user;
        item.data.likes_count = likeData.likes_count;
        item.data.like_id = likeData.like_id;
      }
    },

    handlePostDeleted(postId) {
      // Remove the post from the items array
      this.items = this.items.filter(item => !(item.type === 'post' && item.data.id === postId));
      // Clean up the reference
      delete this.postCards[postId];
    },

    async incrementPostView(postId) {
      try {
        await axios.post(`https://api.6buoys.com/api/post-view/${postId}/`, {}, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });
      } catch (error) {
        console.error(`Error incrementing view count for post ${postId}:`, error);
      }
    },

    goToUserProfile(username) {
      // Navigate to the user's profile page
      this.$router.push(`/${username}`);
    },

    async closeHighlight() {
      if (!this.highlight) return;
      
      try {
        // Call the mark-closed endpoint
        await axios.post(`https://api.6buoys.com/api/highlights/${this.highlight.id}/mark-closed/`, {}, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });
        
        // Hide the highlight after successful API call
        this.highlight = null;
      } catch (error) {
        console.error('Error marking highlight as closed:', error);
        // Still hide the highlight even if the API call fails
        this.highlight = null;
      }
    },

    toggleFilters() {
      this.showFilters = !this.showFilters;
    },

    applyFilters() {
      // Only apply filters if we're in videos timeline mode
      if (this.timelineMode !== 'videos') return;
      
      // Reset timeline state for filtered results
      this.items = [];
      this.offset = 0;
      this.postCards = {};
      this.loadingtimeline = true;
      
      // Fetch items with current filters
      this.fetchItems();
    },

    clearFilters() {
      this.filters = {
        minSpeed: '',
        minRope: ''
      };
      this.applyFilters();
    },

    hasActiveFilters() {
      return this.filters.minSpeed || this.filters.minRope;
    }
  },
};
</script>

<style scoped>
/* General Styles */
body {
  background-color: #15202b;
  color: #d9d9d9;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.timeline {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px 0px 56px 0px;
  background-color: #15202b;
}

/* Posts Container */
.posts-container {
  transition: transform 0.2s ease-out;
}

/* Pull-to-Refresh Styles */
.pull-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 20px;
  margin-bottom: 4px;
  transition: opacity 0.2s ease;
}

.pull-text {
  color: #8899a6;
  font-size: 14px;
  font-weight: 500;
}

/* Highlight Styles */
.highlight-container {
  margin: 0 auto 20px auto;
  max-width: 560px; /* Slightly less wide than posts (600px) */
  background-color: #192734;
  border: 1px solid #1da1f2;
  border-radius: 16px;
  padding: 20px;
  position: relative;
  overflow: hidden;
}

.highlight-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.highlight-message {
  flex: 1;
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
  text-align: center;
}

.highlight-user {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px;
  border-radius: 12px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.highlight-user:hover {
  background-color: rgba(29, 161, 242, 0.1);
  transform: translateY(-1px);
}

.highlight-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid #1da1f2;
  object-fit: cover;
  transition: all 0.2s ease;
}

.highlight-user:hover .highlight-avatar {
  border-color: #5bb3f5;
  box-shadow: 0 4px 8px rgba(29, 161, 242, 0.3);
}

.highlight-username {
  color: #1da1f2;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  transition: color 0.2s ease;
}

.highlight-user:hover .highlight-username {
  color: #5bb3f5;
}

/* Timeline Toggle Styles */
.timeline-toggle {
  display: flex;
  background-color: #192734;
  border-radius: 20px;
  padding: 4px;
  margin-bottom: 20px;
  border: 1px solid #38444d;
  flex-wrap: wrap;
  gap: 8px;
}

.videos-toggle-container {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 100px;
}

.toggle-btn {
  flex: 1;
  padding: 8px 16px;
  border: none;
  border-radius: 16px;
  background-color: transparent;
  color: #8899a6;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  min-height: 40px;
  position: relative;
}

.videos-toggle {
  justify-content: center;
  padding-right: 12px;
  position: relative;
}

.toggle-text {
  flex: none;
  text-align: center;
}

.toggle-btn:hover {
  background-color: rgba(29, 161, 242, 0.1);
  color: #1da1f2;
}

.toggle-btn.active {
  background-color: #1da1f2;
  color: #ffffff;
  font-weight: 600;
}

.toggle-btn.active:hover {
  background-color: #1a91da;
  color: #ffffff;
}

.highlight-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  color: #8899a6;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 1;
}

.highlight-close:hover {
  background-color: rgba(136, 153, 166, 0.1);
  color: #ffffff;
  transform: scale(1.1);
}

.highlight-close svg {
  width: 14px;
  height: 14px;
}

.filter-icon-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: inherit;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  position: absolute;
  right: 12px;
  top: 50%;
  transform: scale(1.5) translateY(-50%);
  margin-top: 5.5px;
}

.filter-icon-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.filter-icon-btn.active {
  background-color: rgba(255, 255, 255, 0.2);
}

.filter-icon-btn.active:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

@media screen and (max-width: 600px) {
  .timeline {
    padding: 20px 4px 56px 4px;
  }
  
  .timeline-toggle {
    margin: 0 4px 20px 4px;
  }
  
  .toggle-btn {
    min-width: auto;
    flex: 1;
  }
  
  .videos-toggle {
    padding-right: 8px;
  }
  
  .filters-container {
    margin: 20px 8px 0 8px;
    padding: 12px;
  }
  
  .filter-row {
    flex-direction: column;
    gap: 12px;
    margin-bottom: 12px;
  }
  
  .filter-icon-btn {
    width: 22px;
    height: 22px;
    padding: 3px;
  }
  
  .filter-icon-btn svg {
    width: 12px;
    height: 12px;
  }
  
  .highlight-container {
    margin: 0 8px 20px 8px;
    max-width: calc(100% - 16px);
    padding: 16px;
  }
  
  .highlight-content {
    gap: 12px;
  }
  
  .highlight-message {
    font-size: 15px;
    text-align: left;
  }
  
  .highlight-avatar {
    width: 40px;
    height: 40px;
  }
  
  .highlight-username {
    font-size: 11px;
  }
}

/* Infinite Scroll Loading */
.infinite-scroll-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0px 20px 20px 20px;
  margin-top: 10px;
}

/* Filter Styles */
.filters-container {
  margin-top: 20px;
  margin-bottom: 20px;
  padding: 16px;
  background-color: #192734;
  border-radius: 16px;
  border: 1px solid #38444d;
}

.filter-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.filter-group {
  flex: 1;
}

.filter-label {
  display: block;
  margin-bottom: 8px;
  color: #8899a6;
  font-size: 14px;
  font-weight: 500;
}

.filter-select {
  width: 100%;
  padding: 8px;
  background-color: #28333e;
  border: 1px solid #38444d;
  border-radius: 8px;
  color: #d9d9d9;
  font-size: 14px;
  font-weight: 500;
}

.filter-select:focus {
  outline: none;
  border-color: #1da1f2;
}

.clear-filters-btn {
  padding: 8px 16px;
  background-color: transparent;
  border: 1px solid #1da1f2;
  border-radius: 8px;
  color: #1da1f2;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.clear-filters-btn:hover {
  background-color: rgba(29, 161, 242, 0.1);
}
</style>
